// Shared US timezone list used across onboarding, invite-accept signup,
// and the Settings → Profile editor. Single source of truth.
//
// IANA names handle DST automatically:
//   - America/New_York is EST in winter, EDT in summer
//   - America/Phoenix and Pacific/Honolulu don't observe DST (their own zones)
//   - America/Adak (Aleutians) does observe DST — distinct from Honolulu
//
// Order is geographic east → west, with non-DST zones grouped logically.

export interface USTimezone {
  value: string
  label: string
}

export const US_TIMEZONES: USTimezone[] = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Phoenix', label: 'Arizona — Mountain, no DST (MST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii — no DST (HST)' },
  { value: 'America/Puerto_Rico', label: 'Atlantic — Puerto Rico, USVI (AST)' },
  { value: 'Pacific/Pago_Pago', label: 'Samoa (SST)' },
]

/**
 * Returns the user's browser-detected timezone if it matches one of our
 * supported US zones. Otherwise falls back to Pacific.
 */
export function detectBrowserTimezone(): string {
  try {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (US_TIMEZONES.some(t => t.value === browserTz)) return browserTz
    return 'America/Los_Angeles'
  } catch {
    return 'America/Los_Angeles'
  }
}

/**
 * Get a human-readable label for a timezone value, or the raw value if not
 * in the standard US list (e.g., a firm using a custom IANA zone).
 */
export function timezoneLabel(value: string | null | undefined): string {
  if (!value) return 'Unknown'
  const found = US_TIMEZONES.find(t => t.value === value)
  return found ? found.label : value
}
